import * as GraphQLClient from "@apollo/client";
import { useCallback, useContext, useMemo, useState } from "react";
import { GetAllPayments, GetAllPrices, GetAllProductions, GetOneAheadProductionsInfo, GetPriceList, GetReservationByLineId, GetReservationsByEmail, UpdateReservation } from "@/graphql/documentNode";
import { Guest, GuestType, MasterUser, Payment, Price, Production, Reservation, Seat, SeatType, Staff, Stage, UpdateReservationDataInput } from "@/graphql/types";
import { useQuery } from "react-query";
import styled from "styled-components";
import Parallelogram from "@/lib/components/common/commonIcon/parallelogram";
import Button from "@/lib/components/common/button/button";
import Modal from "@/lib/components/common/modal/modal";
import GuestInput from "@/lib/components/common/input/guestInput";
import { EMAIL_REGEXP, FREE_STRING_REGEXP, HIRAGANA_REGEXP, REQUIRED_STRING_REGEXP } from "@/lib/components/reservation";
import SelectItem from "@/lib/components/common/input/selectItem";
import dayjs from "dayjs";
import { TextField } from "@mui/material";
import SelectItemV2 from "@/lib/components/common/input/selectItemV2";
import { useSnackBar } from "@/lib/ts/hooks/useSnackBar";
import { AuthUser, getCurrentUser, signOut } from "aws-amplify/auth";
import { Authenticator } from "@aws-amplify/ui-react"
import { useUserRegistration } from "@/lib/ts/hooks/useUserRegistration"
import Header from "@/lib/components/common/header";


const AdminCancel = () => {
    const snackBar = useSnackBar({})
    const snackbar2 = useSnackBar({})
    const [email, setEmail] = useState("")

    const [showUserRegistration, setShowUserRegistration] = useState(false)
    const [currentUser, setCurrentUser] = useState<AuthUser | undefined>(undefined)
    const [user, setUser] = useState<MasterUser | undefined>(undefined)

    const userRegistration = useUserRegistration({
        currentUser,
        setUser,
        setCurrentUser,
        signOut,
        user,
    })


    const [reservations, setReservations] = useState([] as Reservation[])
    const [guests, setGuests] = useState([] as Guest[])
    const [stages, setStages] = useState([] as Stage[])
    const [productions, setProductions] = useState([] as Production[])
    const [staffs, setStaffs] = useState([] as Staff[])
    const [seats, setSeats] = useState([] as Seat[])
    const [seatTypes, setSeatTypes] = useState([] as SeatType[])
    const [guestTypes, setGuestTypes] = useState([] as GuestType[])
    const [prices, setPrices] = useState([] as Price[])

    const [showReservationModal, setShowReservationModal] = useState(-1)

    const [cancellableReservation, setCancellableReservation] = useState(undefined as undefined | Reservation)

    const [payments, setPayments] = useState([] as Payment[])

    
    
    const [getReservationsByEmail] = GraphQLClient.useLazyQuery(GetReservationsByEmail, {
        onCompleted: (data) => {
            const item = data.getReservationsByEmail
            console.log(item)
            setReservations(item.reservations)
            setGuestTypes(item.guestTypes)
            setSeatTypes(item.seatTypes)
            setPayments(item.payments)
            setPrices(item.prices)
            setStaffs(item.staffs)
            setProductions(item.productions)
            setStages(item.stages)
            setSeats(item.seats)
            setGuests(item.guests)
            snackBar.onQuerySuccess()
        },
        onError: (err) => {
            console.log(err)
            snackBar.onQueryError()
        },
        fetchPolicy: "network-only"
    })

    const getAllData = useCallback(async () => {
        if(!email) {
            return
        }
        snackBar.loadStart()
        await getReservationsByEmail({
            variables: {
                email,
            }
        })
    }, [email, getReservationsByEmail, snackBar])


    const [updateReservation] = GraphQLClient.useMutation(UpdateReservation, {
        onCompleted: async (data) => {
            snackBar.onMutationSuccess()
        },
        onError: (error) => {
            console.log(error)
            snackBar.onMutationError()
        },
    })

    const updateReservationCallback = useCallback(async (reservation: Reservation, guests: Guest[]) => {
        snackBar.loadStart()
        const reservationCopy: UpdateReservationDataInput = {
            id: reservation.id ?? -1,
            confirmed: reservation.confirmed,
            email: reservation.email,
            invitationId: reservation.invitationId,
            name: reservation.name,
            note: reservation.note,
            payment: reservation.payment,
            phonetic: reservation.phonetic,
            stageId: reservation.stageId,
            usePoint: reservation.usePoint,
        }
        console.log(reservationCopy)
        const guestsCopy: Guest[] = guests.map(guest => ({
            guestType: guest.guestType,
            reservationId: guest.reservationId,
            id: guest.id,
            seatId: guest.seatId,
        }))
        await updateReservation({
            variables: {
                input:{
                    reservation: reservationCopy,
                    guests: guestsCopy,
                }
            }
        })
        setShowReservationModal(-1)
        await getAllData()
    }, [getAllData, snackBar, updateReservation])


    const useFixedReservation = (submit: (reservation: Reservation, guests: Guest[]) => void) => {
        const [fixedReservation, setFixedReservation] = useState(undefined as Reservation | undefined)
        const [fixedGuests, setFixedGuests] = useState([] as Guest[])
        const [staffs, setStaffs] = useState([] as Staff[])


        const [cancellableGuestIds, setCancellableGuestIds] = useState([] as number[])

        const [invalids, setInvalids] = useState({} as { [key: string]: boolean })
        const onChange = useCallback((key: string, value: string, error: boolean) => {
            const invalidsCopy = {...invalids}
            invalidsCopy[key] = error
            setInvalids(invalidsCopy)

            if(!error) {
                const reservationCopy: any = {...fixedReservation}
                reservationCopy[key] = value
                setFixedReservation(reservationCopy)
            }
        }, [fixedReservation, invalids])
        

        const staff = useMemo(() => staffs.find((staff) => staff.id === fixedReservation?.invitationId), [fixedReservation?.invitationId, staffs])
        

        const init = useCallback((reservation: Reservation, guests: Guest[], givenStaffs: Staff[], productionId: number) => {
            setStaffs([...givenStaffs.filter(staff => staff.productionId === productionId), { id: -1, name: 'なし', phonetic: '', productionId, stageId: -1, }])
            setFixedReservation(reservation)
            setFixedGuests(guests.filter(guest => guest.reservationId === reservation.id))
        }, [])

        const onSubmit = useCallback(() => {
            if(fixedReservation) {
                const finalGuests = fixedGuests.filter(guest => cancellableGuestIds.indexOf(guest.id!) === -1)
                submit(fixedReservation, finalGuests)
            }
            setShowReservationModal(-1)
        }, [cancellableGuestIds, fixedGuests, fixedReservation, submit])

        const selectedStaffIndex = useMemo(() => {
            if(staff) {
                return staffs.findIndex(s => s.id === staff.id)
            }
            return -1
        }, [staff, staffs])

        const component = useMemo(() => {
            return fixedReservation ? (
                <Modal
                    onClose={() => {setShowReservationModal(-1)}}
                >
                    <FixedReservationStyle>
                        <div className="guest-information">
                            <div className="set">
                                <div className="key">
                                    <Parallelogram
                                        bodyColor="red"
                                        shadowColor="black"
                                        width={250}
                                    >
                                        お名前
                                    </Parallelogram>
                                </div>
                                <div className="value">
                                    <GuestInput 
                                        defaultValue={ fixedReservation.name }
                                        error={invalids.name}
                                        errorMessage="記号は使用できません"
                                        onChange={(value, errorOccurs) => {
                                            onChange("name", value, errorOccurs)
                                        }}
                                        label=""
                                        placeholder="お名前"
                                        id="name"
                                        literalValidation={REQUIRED_STRING_REGEXP}
                                    />
                                </div>
                            </div>
                            <div className="set">
                                <div className="key">
                                    <Parallelogram
                                        bodyColor="red"
                                        shadowColor="black"
                                        width={250}
                                    >
                                            ふりがな
                                    </Parallelogram>
                                </div>
                                <div className="value">
                                    <GuestInput 
                                        defaultValue={ fixedReservation.phonetic }
                                        error={invalids.phonetic}
                                        errorMessage="ひらがなのみ使用できます"
                                        onChange={(value, errorOccurs) => {
                                            onChange("phonetic", value, errorOccurs)
                                        }}
                                        label=""
                                        placeholder="ふりがな"
                                        id="phonetic"
                                        literalValidation={HIRAGANA_REGEXP}
                                    />
                                </div>
                            </div>
                            <div className="set">
                                <div className="key">
                                    <Parallelogram
                                        bodyColor="red"
                                        shadowColor="black"
                                        width={250}
                                    >
                                        メールアドレス
                                    </Parallelogram>
                                </div>
                                <div className="value">
                                    <GuestInput 
                                        defaultValue={ fixedReservation.email }
                                        error={invalids.email}
                                        errorMessage="正しいメールアドレスを入力してください"
                                        onChange={(value, errorOccurs) => {
                                            onChange("phonetic", value, errorOccurs)
                                        }}
                                        label=""
                                        placeholder="メールアドレス"
                                        id="email"
                                        literalValidation={EMAIL_REGEXP}
                                    />
                                </div>
                            </div>
                            <div className="set">
                                <div className="key">
                                <Parallelogram
                                    bodyColor="red"
                                    shadowColor="black"
                                    width={250}
                                >
                                    招待者
                                </Parallelogram>
                                </div>
                                <div className="select">
                                    <SelectItemV2
                                        width={250}
                                        items={staffs}
                                        selectedIndex={selectedStaffIndex}
                                        changeIndex={(item, _) => { setFixedReservation({...fixedReservation, invitationId: item.id}) }}
                                    />
                                </div>
                            </div>
                            <div className="set">
                                <div className="key">
                                    <Parallelogram
                                        bodyColor="red"
                                        shadowColor="black"
                                        width={250}
                                    >
                                        券種・座席
                                    </Parallelogram>
                                </div>
                                <div className="instruction">チケットを選択して「送信」でキャンセルできます。</div>
                                {
                                    fixedGuests.map((member, index) => {
                                        const seat = seats.find(seat => seat.id === member.seatId)
                                        const seatType = seatTypes.find(type => type.id === seat?.type) || {name: '一般'}
                                        const guestType = guestTypes.find(type => type.id === member.guestType) || {name: '一般'}
                                        return (
                                            <div key={"member-" + index} className="value button">
                                                <Button 
                                                    label={`${ guestType?.name }: ${ seat?.seatName || '' }（${ seatType?.name }）`}
                                                    buttonType={ member.id && cancellableGuestIds.indexOf(member.id) > -1 ? "checked" : "powerful-red" }
                                                    onClick={() => {
                                                        if(member.id) {
                                                            const idsCopy = [...cancellableGuestIds]
                                                            const idIndex = idsCopy.indexOf(member.id)
                                                            if(idIndex > -1) {
                                                                idsCopy.splice(idsCopy.indexOf(member.id))
                                                            } else {
                                                                idsCopy.push(member.id)
                                                            }
                                                            setCancellableGuestIds(idsCopy)
                                                        }
                                                    }}
                                                    width={220}
                                                />
                                            </div>
                                        )
                                    })
                                }
                            </div>
                            <div className="set">
                                <div className="key">
                                    <Parallelogram
                                        bodyColor="red"
                                        shadowColor="black"
                                        width={250}
                                    >
                                        備考
                                    </Parallelogram>
                                </div>
                                <div className="value">
                                    <GuestInput 
                                        defaultValue={ fixedReservation.note || '' }
                                        error={invalids.note}
                                        errorMessage="記号は使用できません"
                                        onChange={(value, errorOccurs) => {
                                            onChange("note", value, errorOccurs)
                                        }}
                                        label=""
                                        placeholder="備考"
                                        id="note"
                                        literalValidation={FREE_STRING_REGEXP}
                                        multiLine
                                        outlined
                                    />
                                </div>
                            </div>
                            <div className="close">
                                <div>
                                    <Button 
                                        label={"送信"}
                                        buttonType="powerful-red"
                                        onClick={onSubmit}
                                    />
                                </div>
                                <div>
                                    <Button 
                                        label={"CLOSE"}
                                        buttonType="powerful-red"
                                        onClick={() => { setShowReservationModal(-1) }}
                                    />
                                </div>
                            </div>
                        </div>
                    </FixedReservationStyle>
                </Modal>
            ) : <></>
        }, [cancellableGuestIds, fixedGuests, fixedReservation, invalids.email, invalids.name, invalids.note, invalids.phonetic, onChange, onSubmit, selectedStaffIndex, staffs])


        return {
            init,
            component,
        }
    }

    const fixedReservation = useFixedReservation(updateReservationCallback)

    const reservationComponents = useMemo(() => {
        return productions.length ? productions.map((production, index) => {
            const stagesOfProduction = stages.filter((stage) => stage.productionId === production.id)
            const components = stagesOfProduction.flatMap((stage) => {
                const reservationsOfStage = reservations.filter((reservation) => reservation.stageId === stage.id && stage.endDateTime > dayjs().unix()).sort((a, b) => {
                    return a.stageId > b.stageId ? 1 : a.stageId < b.stageId ? -1 : 0
                })
                return reservationsOfStage.length ? (
                    reservationsOfStage.flatMap((reservation => {
                        const guestsOfReservation = guests.filter(guest => guest.reservationId === reservation.id)
                        return (
                            <div key={"reservation-stage-" + index} className="reservation-stage">
                                <Button 
                                    buttonType="powerful-red-white-shadow"
                                    label={`${stage.stageName}: ${guestsOfReservation.length}人`}
                                    onClick={() => {
                                        setShowReservationModal(reservation.id || -1)
                                    }}
                                    width={250}
                                />
                            </div>
                        )
                    }))
                ) : <></>
            })
            return components.length ? (
                <div 
                    className="reservations"
                >
                    <div className="reservation-production">
                        <Parallelogram
                            bodyColor="red"
                            shadowColor="white"
                        >
                            {production.name}
                        </Parallelogram>
                    </div>
                    {components}
                </div>
            ) : <></>
        }) : <></>
    }, [guests, productions, reservations, stages])

    const reservationModal = useMemo(() => {
        const reservation = reservations.find((item) => item.id === showReservationModal)
        const staff = staffs.find((staff) => staff.id === reservation?.invitationId)
        const members = guests.filter((guest) => guest.reservationId === reservation?.id)

        const priceArray = guests.filter(guest => guest.reservationId === reservation?.id).map((guest) => {
            const guestType = guest.guestType
            const seatType = seats.find(seat => seat.id === guest.seatId)?.type
            const price = prices.find(price => price.guestType === guestType && price.seatType === seatType)
            return price?.price
        })
        const price = priceArray.length ? priceArray.reduce((pv, cv) => (pv || 0) + (cv || 0)) || 0 : 0

        const stage = stages.find(stage => stage.id === reservation?.stageId)
        const production = productions.find(production => stage ? production.id === stage.productionId : undefined)
        console.log(production)
        return reservation ? (
            <Modal
                onClose={() => setShowReservationModal(-1)}
            >
                <div className="guest-information">
                    {production ? 
                    <div className="set">
                        <div className="key">
                            <Parallelogram
                                bodyColor="red"
                                shadowColor="black"
                                width={250}
                            >
                                公演Webサイト
                            </Parallelogram>
                        </div>
                        <div className="value">
                            <Button 
                                buttonType="powerful-red"
                                label="サイトを開く"
                                onClick={() => { window.open(production.url) }}
                            />
                        </div>
                    </div> : <></>}
                    {
                        production &&production.locationLink ? (
                            <div className="set">
                                <div className="key">
                                    <Parallelogram
                                        bodyColor="red"
                                        shadowColor="black"
                                        width={250}
                                    >
                                        会場
                                    </Parallelogram>
                                </div>
                                <div className="value">
                                    <Button 
                                        buttonType="powerful-red"
                                        label="会場リンク"
                                        onClick={() => { window.open(production.location_url) }}
                                    />
                                    <div>{production.location}</div>
                                </div>
                            </div>
                        ) : <></>
                    }
                    <div className="set">
                        <div className="key">
                            <Parallelogram
                                bodyColor="red"
                                shadowColor="black"
                                width={250}
                            >
                                お名前
                            </Parallelogram>
                        </div>
                        <div className="value">{ reservation.name }</div>
                    </div>
                    <div className="set">
                        <div className="key">
                            <Parallelogram
                                bodyColor="red"
                                shadowColor="black"
                                width={250}
                            >
                                    ふりがな
                            </Parallelogram>
                        </div>
                        <div className="value">{ reservation.phonetic }</div>
                    </div>
                    <div className="set">
                        <div className="key">
                            <Parallelogram
                                bodyColor="red"
                                shadowColor="black"
                                width={250}
                            >
                                メールアドレス
                            </Parallelogram>
                        </div>
                        <div className="value">{ reservation.email }</div>
                    </div>
                    <div className="set">
                        <div className="key">
                        <Parallelogram
                            bodyColor="red"
                            shadowColor="black"
                            width={250}
                        >
                            招待者
                        </Parallelogram>
                        </div>
                        <div className="value">{ staff?.name || 'なし' }</div>
                    </div>
                    <div className="set">
                        <div className="key">
                            <Parallelogram
                                bodyColor="red"
                                shadowColor="black"
                                width={250}
                            >
                                券種・座席
                            </Parallelogram>
                        </div>
                        {
                            members.map((member, index) => {
                                const seat = seats.find(seat => seat.id === member.seatId)
                                const seatType = seatTypes.find(type => type.id === seat?.type) || {name: '一般'}
                                const guestType = guestTypes.find(type => type.id === member.guestType) || {name: '一般'}
                                return (
                                    <div key={"member-" + index} className="value">
                                        { guestType?.name }: { seat?.seatName }（{ seatType?.name }）
                                    </div>
                                )
                            })
                        }
                    </div>
                    {
                        !price ? <></> : reservation.confirmed ? (
                            <div className="set">
                                <div className="key">
                                    <Parallelogram
                                        bodyColor="red"
                                        shadowColor="black"
                                        width={250}
                                    >
                                        支払
                                    </Parallelogram>
                                </div>
                                <div className="value"></div>
                            </div>
                        ) : (
                            <>  
                                <div className="set">
                                    <div className="key">
                                        <Parallelogram
                                            bodyColor="red"
                                            shadowColor="black"
                                            width={250}
                                        >
                                            チケット料金
                                        </Parallelogram>
                                    </div>
                                    <div className="value">
                                        {price}円
                                    </div>
                                </div>
                                <div className="set">
                                    <div className="key">
                                        <Parallelogram
                                            bodyColor="red"
                                            shadowColor="black"
                                            width={250}
                                        >
                                            支払方法
                                        </Parallelogram>
                                    </div>
                                    <div className="value">{payments.find(item => item.id === reservation.payment)?.name || "-"}</div>
                                    <div className="value">{payments.find(item => item.id === reservation.payment)?.description || "-"}</div>
                                    {
                                        payments.find(item => item.id === reservation.payment)?.link ? (
                                            <Button 
                                                buttonType="powerful-red"
                                                label="支払う"
                                                onClick={() => {window.open(payments.find(item => item.id === reservation.payment)?.link || "")}}
                                            />
                                        ) : <></>
                                    }
                                </div>
                            </>
                        )
                    }
                    <div className="set">
                        <div className="key">
                            <Parallelogram
                                bodyColor="red"
                                shadowColor="black"
                                width={250}
                            >
                                備考
                            </Parallelogram>
                        </div>
                        <div className="value">{ reservation.note || 'なし' }</div>
                    </div>
                    <div className="close">
                        <Button 
                            width={70}
                            label={"変更"}
                            buttonType="powerful-red"
                            onClick={() => { 
                                fixedReservation.init(reservation, guests, staffs, production?.id || -1)
                                setShowReservationModal(-2) 
                            }}
                            disabled={!reservation || reservation.id === undefined}
                        />
                        <Button 
                            width={90}
                            label={"予約取消"}
                            buttonType="powerful-red"
                            onClick={() => { 
                                setCancellableReservation(reservation)
                                setShowReservationModal(-3)
                            }}
                            disabled={!reservation || reservation.id === undefined}
                        />
                        <Button 
                            label={"CLOSE"}
                            buttonType="powerful-red"
                            onClick={() => { setShowReservationModal(-1) }}
                        />
                    </div>
                    <p className="cancel-caution">※予約取消の際、キャンセル料等が発生する場合がございます。詳しくは公演ウェブサイト等をご覧いただくか、公演運営者にお問い合わせください。</p>
                    <p className="cancel-caution">※予約取消後、ご登録いただいたメールアドレス宛に公演運営者より返金・キャンセル料に関するご連絡をお送りする場合がございます。</p>
                </div>
            </Modal>
        ) : <></>
    }, [fixedReservation, guestTypes, guests, payments, prices, productions, reservations, seatTypes, seats, showReservationModal, staffs, stages])

    const cancelConfirmModal = useMemo(() => {
        return (
            <Modal
                onClose={() => { setShowReservationModal(-1) }}
            >
                <ConfirmStyle>
                    <div className="title">
                        <Parallelogram
                            bodyColor="red"
                            shadowColor="black"
                            width={200}
                        >
                            キャンセル確認
                        </Parallelogram>
                    </div>
                    <div className="confirm">
                        この予約をキャンセルしてもよろしいですか？
                    </div>
                    <div className="buttons">
                        <div className="button">
                            <Button 
                                width={150}
                                buttonType="powerful-red"
                                label="キャンセルする"
                                onClick={async () => {
                                    if(cancellableReservation) {
                                        await updateReservationCallback(cancellableReservation, [])
                                    }
                                }}
                            />
                        </div>
                        <div className="button">
                            <Button 
                                width={150}
                                buttonType="powerful-red"
                                label="やめる"
                                onClick={() => { setShowReservationModal(-1) }}
                            />
                        </div>
                    </div>
                </ConfirmStyle>
            </Modal>
        )
    }, [cancellableReservation, updateReservationCallback])

    const emailInput = useMemo(() => {
        return (
            <EmailInputStyle>
                <p className="email-title">予約確認/キャンセル</p>
                <p className="email-description">予約時に指定したメールアドレスを入力してください。</p>
                <div className="email-text">
                    <TextField 
                        onChange={(event) => { setEmail(event.target.value) }}
                        variant="standard"
                        placeholder="example@gmail.com"
                    />
                </div>
                <div>
                    <Button 
                        onClick={getAllData}
                        buttonType="powerful-red"
                        label="検索"
                        disabled={!email}
                    />
                </div>
            </EmailInputStyle>
        )
    }, [email, getAllData])

    return (
        <Authenticator>
            {
                ({signOut, user: signInUser}) => {
                    setCurrentUser(signInUser)
                    return currentUser?.signInDetails ? (
                        <>
                        <Header />
                            <IndexStyle
                                style={{ position: showReservationModal !== -1 ? "fixed" : "static" }}
                            >
                                {emailInput}
                                {showReservationModal === -2 ? fixedReservation.component : <></>}
                                {reservationComponents}
                                {reservationModal}
                                {showReservationModal === -3 ? cancelConfirmModal : <></>}
                                {snackBar.component}
                            </IndexStyle>
                        </>
                    ) : <div>
                    <Button
                        buttonType="powerful-red-white-shadow"
                        label="サインイン "
                        onClick={() => { signOut?.() }}
                    />
                </div>
                }
            }
        </Authenticator>
    )
}

const IndexStyle = styled.div`
margin-top: 100px;
background-color: black;
width: 100%;
    .reservations {
        margin: 30px auto 30px auto;
        .reservation-production {
            width: 320px;
            margin: 30px auto 30px auto;
        }
        .reservation-stage {
            width: 320px;
            margin: 20px auto 20px auto;
            text-align: center;
        }
    }
    .guest-information {
        color: black;
        .set {
            .instruction {
                font-size: 12px;
                margin: 10px auto 10px auto;
            }
            .key {
                width: 250px;
                color: white;
                margin: 20px auto 20px auto;
            }
            .value {
                margin: 10px auto 10px auto;
                text-align: center;
                &.button {
                    margin: 10px auto 10px auto;
                }
            }
        }
        .close {
            display: flex;
            justify-content: center;
            gap: 10px;
            margin: 20px auto 10px auto;
        }
        .select {
            width: 250px;
            margin: auto;
            text-align: center;
        }
        .cancel-caution {
            font-size: 12px;
        }
    }
`

const FixedReservationStyle = styled.div`
    
`

const ConfirmStyle = styled.div`
.confirm {    
    color: black;
}
.title {
    width: 200px;
    margin: 0 auto 30px auto;
}
.buttons {
    .button {
        margin: 10px auto 10px auto;
    }
}
`

const EmailInputStyle = styled.div`
    background-color: white;
    border-radius: 8px;
    padding: 16px;
    text-align: center;
    color: black;
    .email-title {
        font-size: 18px;
        font-weight: 700;
        margin-bottom: 8px;
    }
    .email-text {
        margin-bottom: 8px;
    }
`

export default AdminCancel